/**
 * Stripe Service
 * 
 * Handles Stripe Checkout integration for subscription payments.
 * Configure via environment variables:
 * - VITE_STRIPE_PUBLISHABLE_KEY: Stripe publishable key
 * - VITE_API_URL: Backend API URL (for creating checkout sessions)
 */

// @stripe/stripe-js is NOT imported at the module level.
// It is loaded on-demand the first time getStripe() is called (i.e. when the user
// initiates checkout). This keeps the initial JS bundle smaller for all pages
// that import from this service without ever reaching checkout.

const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY
import { API_BASE_URL } from '../config'

let stripePromise = null

/**
 * Get (or lazily initialize) the Stripe instance.
 * @returns {Promise<Object|null>} Stripe instance or null if not configured
 */
export const getStripe = async () => {
  if (!STRIPE_PUBLISHABLE_KEY) {
    console.warn('Stripe publishable key not configured. Set VITE_STRIPE_PUBLISHABLE_KEY environment variable.')
    return null
  }
  if (!stripePromise) {
    const { loadStripe } = await import('@stripe/stripe-js')
    stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY)
  }
  return await stripePromise
}

/**
 * Create a Stripe Checkout session for subscription
 * 
 * This should be called from your backend API. For MVP, we'll create a simple
 * endpoint that creates a checkout session. In production, this should be
 * handled server-side for security.
 * 
 * @param {string} tier - Subscription tier or add-on product name
 * @param {string} userId - Supabase user ID (optional, for logged-in users)
 * @param {string} userEmail - User email (optional, for pre-filling checkout)
 * @returns {Promise<Object>} { sessionId, url, error }
 */
const VALID_TIERS = [
  'homeowner_wealth', 'buyer_3month', 'pro_monthly',
  'buyer_seller', 'investor',
  'addon_single', 'addon_5pack', 'addon_10pack',
  'quick_unlock',
  'quick_predict',
]

/**
 * Create a Stripe Checkout session.
 *
 * @param {string} tier - Product tier or add-on name
 * @param {string|null} userId - Supabase user ID (optional — guests can purchase quick_unlock without one)
 * @param {string|null} userEmail - Pre-fills the Stripe checkout email field
 * @param {string|null} propertyIdentifier - URL slug for the property (required for quick_unlock)
 * @returns {Promise<{sessionId, url, error}>}
 */
export const createCheckoutSession = async (
  tier,
  userId = null,
  userEmail = null,
  propertyIdentifier = null,
  quickPredictContext = null,
) => {
  if (!tier || !VALID_TIERS.includes(tier)) {
    return { sessionId: null, url: null, error: { message: `Invalid product. Please try again.` } }
  }

  try {
    const shareRef = localStorage.getItem('share_ref')

    // Quick Unlock returns to the property page so the user immediately sees their unlock
    const isQuickUnlock = tier === 'quick_unlock'
    const isQuickPredict = tier === 'quick_predict'
    const successUrl = isQuickUnlock && propertyIdentifier
      ? `${window.location.origin}/property/${propertyIdentifier}?unlock_success=true&session_id={CHECKOUT_SESSION_ID}`
      : isQuickPredict
        ? `${window.location.origin}/predict?quick_predict_success=true&session_id={CHECKOUT_SESSION_ID}`
      : `${window.location.origin}/pricing?success=true&session_id={CHECKOUT_SESSION_ID}`
    const cancelUrl = isQuickUnlock && propertyIdentifier
      ? `${window.location.origin}/property/${propertyIdentifier}?canceled=true`
      : isQuickPredict
        ? `${window.location.origin}/predict?quick_predict_canceled=true`
      : `${window.location.origin}/pricing?canceled=true`

    const body = {
      tier,
      userId,
      userEmail,
      successUrl,
      cancelUrl,
    }
    if (shareRef) body.shareRef = shareRef
    if (propertyIdentifier) body.propertyIdentifier = propertyIdentifier
    if (isQuickPredict && quickPredictContext?.eligibleCity) {
      body.eligibleCity = quickPredictContext.eligibleCity
    }
    if (isQuickPredict && quickPredictContext?.eligiblePropertyType) {
      body.eligiblePropertyType = quickPredictContext.eligiblePropertyType
    }

    const response = await fetch(`${API_BASE_URL}/api/v1/stripe/create-checkout-session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.detail || errorData.message || `HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    if (shareRef) localStorage.removeItem('share_ref')
    return { sessionId: data.sessionId, url: data.url, error: null }
  } catch (error) {
    console.error('Error creating checkout session:', error)
    return { sessionId: null, url: null, error: { message: error.message || 'Failed to create checkout session' } }
  }
}

/**
 * Redirect to Stripe Checkout
 * 
 * @param {string} tier - Subscription tier
 * @param {string} userId - Supabase user ID (optional)
 * @param {string} userEmail - User email (optional)
 * @returns {Promise<Object>} { error }
 */
export const redirectToCheckout = async (
  tier,
  userId = null,
  userEmail = null,
  propertyIdentifier = null,
  quickPredictContext = null,
) => {
  const stripe = await getStripe()
  if (!stripe) {
    return { error: { message: 'Stripe not configured. Please set VITE_STRIPE_PUBLISHABLE_KEY.' } }
  }

  const { sessionId, url, error } = await createCheckoutSession(
    tier,
    userId,
    userEmail,
    propertyIdentifier,
    quickPredictContext,
  )
  
  if (error) {
    return { error }
  }

  if (!url) {
    return { error: { message: 'No checkout URL returned from server' } }
  }

  // Redirect to Stripe Checkout
  // Note: In production, you might want to use sessionId with stripe.redirectToCheckout()
  // For now, we'll redirect directly to the URL returned from the backend
  window.location.href = url

  return { error: null }
}

/**
 * Verify checkout session and update subscription status
 * This should be called after successful checkout redirect
 * 
 * @param {string} sessionId - Stripe Checkout session ID
 * @returns {Promise<Object>} { subscription, error }
 */
export const verifyCheckoutSession = async (sessionId) => {
  if (!sessionId) {
    return { subscription: null, error: { message: 'Session ID is required' } }
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/v1/stripe/verify-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ sessionId })
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.detail || errorData.message || `HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    return {
      subscription: data.subscription || null,
      purchase: data.purchase || null,
      quickUnlock: data.quick_unlock || null,
      error: null,
    }
  } catch (error) {
    console.error('Error verifying checkout session:', error)
    return { subscription: null, purchase: null, quickUnlock: null, error: { message: error.message || 'Failed to verify checkout session' } }
  }
}

/**
 * Link a Quick Unlock purchase (made as a guest) to a newly-created account.
 * Called after the guest signs up with email/password post-payment.
 *
 * @param {string} stripeSessionId - The Stripe Checkout session ID from the URL
 * @param {string} userId - The newly-created Supabase user ID
 * @returns {Promise<{propertyIdentifier, error}>}
 */
export const linkGuestUnlock = async (stripeSessionId, userId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/v1/stripe/link-unlock`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ stripeSessionId, userId }),
    })
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.detail || errorData.message || `HTTP error! status: ${response.status}`)
    }
    const data = await response.json()
    return { propertyIdentifier: data.property_identifier, error: null }
  } catch (error) {
    console.error('Error linking guest unlock:', error)
    return { propertyIdentifier: null, error: { message: error.message || 'Failed to link unlock to account' } }
  }
}

/**
 * Link a Quick Predict purchase (made as a guest) to a newly-created account.
 *
 * @param {string} stripeSessionId - The Stripe Checkout session ID from the URL
 * @param {string} userId - The Supabase user ID
 * @returns {Promise<{creditsGranted:number|null, reportSaved:boolean, error:Object|null}>}
 */
export const linkGuestQuickPredict = async (stripeSessionId, userId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/v1/stripe/link-quick-predict`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ stripeSessionId, userId }),
    })
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.detail || errorData.message || `HTTP error! status: ${response.status}`)
    }
    const data = await response.json()
    return { creditsGranted: data.credits_granted ?? null, reportSaved: !!data.report_saved, error: null }
  } catch (error) {
    console.error('Error linking guest quick predict:', error)
    return { creditsGranted: null, reportSaved: false, error: { message: error.message || 'Failed to link quick predict purchase' } }
  }
}

/**
 * Open Stripe Customer Portal for managing/canceling subscription.
 * Requires the user to be signed in (Bearer token).
 *
 * @param {string} accessToken - Current user's JWT access token
 * @param {string} [returnUrl] - URL to return to after portal (defaults to /pricing)
 * @returns {Promise<Object>} { url, error }
 */
export const createPortalSession = async (accessToken, returnUrl) => {
  if (!accessToken) {
    return { url: null, error: { message: 'Sign in required' } }
  }
  const returnTo = returnUrl || `${window.location.origin}/pricing`
  try {
    const response = await fetch(`${API_BASE_URL}/api/v1/stripe/portal-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ returnUrl: returnTo }),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.detail || errorData.message || `HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    return { url: data.url, error: null }
  } catch (error) {
    console.error('Error creating portal session:', error)
    return { url: null, error: { message: error.message || 'Failed to open billing portal' } }
  }
}

// Export Stripe configuration for debugging
export const stripeConfig = {
  hasPublishableKey: !!STRIPE_PUBLISHABLE_KEY,
  isConfigured: !!STRIPE_PUBLISHABLE_KEY
}
